import { HowToAnswer } from "./HowToAnswer";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/**
 * ScenarioChoice — a short situation, followed by a small set of realistic
 * responses. Each response carries its own scoring payload; the caller
 * decides what to do with the picked option's `values`.
 *
 * The situation is written in the second person, present tense, and never
 * hints at a "correct" answer. If a response reads as morally superior,
 * rewrite until every option costs the learner something.
 *
 * The pick is a two-step: tap the answer, see it held, then press Continue.
 * Nobody should have to guess whether a tap registered.
 */

export interface ScenarioOption<V = Record<string, number | string>> {
  id: string;
  label: string;
  detail?: string;
  /** Scoring payload consumed by the wiring code. */
  values: V;
}

export interface ScenarioChoiceProps<V = Record<string, number | string>> {
  situation: string;
  eyebrow?: string;
  options: ScenarioOption<V>[];
  onPick: (option: ScenarioOption<V>) => void;
  continueLabel?: string;
}

export function ScenarioChoice<V = Record<string, number | string>>({
  situation, eyebrow = "The situation", options, onPick, continueLabel = "Continue",
}: ScenarioChoiceProps<V>) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = options.find((o) => o.id === selectedId) ?? null;

  return (
    <div className="space-y-6">
      <HowToAnswer>Pick the one option closest to what you'd really do.</HowToAnswer>
      <div className="rounded-3xl border border-border bg-card/60 p-5 md:p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">{eyebrow}</div>
        <p className="text-lg md:text-xl leading-relaxed text-foreground/90">{situation}</p>
      </div>

      <div className="space-y-2">
        {options.map((r, i) => {
          const isSelected = r.id === selectedId;
          return (
            <motion.button
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              aria-pressed={isSelected}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.24 }}
              whileHover={{ x: 4 }}
              className={[
                "w-full text-left rounded-2xl border p-4 md:p-5 min-h-[64px]",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                isSelected
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:border-primary/70 hover:bg-primary/5",
              ].join(" ")}
            >
              <div className="font-medium">{r.label}</div>
              {r.detail && <div className="text-sm text-muted-foreground mt-1">{r.detail}</div>}
            </motion.button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => selected && onPick(selected)}
          disabled={!selected}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            inline-flex items-center gap-2
            disabled:opacity-40 disabled:pointer-events-none
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {continueLabel} <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

